/**
 * Manual Sale Collection for the monthly import report (one amount per month + branch).
 * Applies only to that month — later months keep their own import/manual value.
 */
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";

export const COL_MONTHLY_SALE_COLLECTIONS = "monthly_sale_collections";

export function monthlySaleCollectionDocId(year, monthIndex, branchFilter = "Main") {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const branch = String(branchFilter || "Main");
  return `${y}-${mi}-${branch}`;
}

export function parseSaleCollectionAmount(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const n = Number(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

export async function loadMonthlySaleCollection(year, monthIndex, branchFilter = "Main") {
  const docId = monthlySaleCollectionDocId(year, monthIndex, branchFilter);
  const snap = await getDoc(doc(db, COL_MONTHLY_SALE_COLLECTIONS, docId));
  if (!snap.exists()) return null;
  const d = snap.data() || {};
  const amount = parseSaleCollectionAmount(d.amount);
  if (amount == null) return null;
  return {
    id: snap.id,
    year: Number(d.year) || Number(year),
    monthIndex: Number.isFinite(Number(d.monthIndex)) ? Number(d.monthIndex) : Number(monthIndex),
    branchFilter: String(d.branchFilter || branchFilter),
    amount,
    updatedAt: d.updatedAt ?? null,
    updatedBy: String(d.updatedBy || ""),
  };
}

export async function saveMonthlySaleCollection({
  year,
  monthIndex,
  branchFilter = "Main",
  amount,
  updatedBy = "",
}) {
  const parsed = parseSaleCollectionAmount(amount);
  if (parsed == null) throw new Error("Enter Sale Collection amount");
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const docId = monthlySaleCollectionDocId(y, mi, branchFilter);
  const payload = {
    year: y,
    monthIndex: mi,
    branchFilter: String(branchFilter || "Main"),
    amount: parsed,
    updatedAt: serverTimestamp(),
    updatedBy: String(updatedBy || ""),
  };
  await setDoc(doc(db, COL_MONTHLY_SALE_COLLECTIONS, docId), payload, { merge: true });
  return { docId, ...payload };
}

export async function clearMonthlySaleCollection(year, monthIndex, branchFilter = "Main") {
  const docId = monthlySaleCollectionDocId(year, monthIndex, branchFilter);
  await deleteDoc(doc(db, COL_MONTHLY_SALE_COLLECTIONS, docId));
}

export function applySaleCollectionToReport(report, amount) {
  if (!report?.collection) return report;
  const n = parseSaleCollectionAmount(amount);
  if (n == null) return report;
  report.collection.saleCollection = n;
  const cashSale = Number(report.collection.cashSale ?? report.totals?.cashSale) || 0;
  const lessCashDiscount = Number(report.collection.lessCashDiscount) || 0;
  const lessSaleReturn = Number(report.collection.lessSaleReturn) || 0;
  const totalCashInHand = cashSale + n - lessCashDiscount - lessSaleReturn;
  report.collection.totalCashInHandParens = Math.abs(totalCashInHand);
  return report;
}
